// /Users/donovan68/dva-frontend-vite/src/components/personalize/PersonalizationWelcomeCard.tsx
import React from 'react';
import { CheckCircle2, Sparkles, ArrowRight, Building2, ClipboardCheck, FileStack, ExternalLink } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

interface PersonalizationWelcomeCardProps {
  profilePercent: number;
  surveyPercent: number;
  documentCount: number;
  onGenerateSummary: () => void;
  onEditProfile?: () => void;
  onContinueSurveys?: () => void;
  onManageDocuments?: () => void;
  isGenerating?: boolean;
}

export function PersonalizationWelcomeCard({
  profilePercent,
  surveyPercent,
  documentCount,
  onGenerateSummary,
  onEditProfile,
  onContinueSurveys,
  onManageDocuments,
  isGenerating = false
}: PersonalizationWelcomeCardProps) {
  
  // Calculate overall completion
  const hasDocuments = documentCount > 0;
  const overallPercent = Math.round((profilePercent + surveyPercent + (hasDocuments ? 100 : 0)) / 3);
  const canGenerate = overallPercent >= 75;
  
  const steps = [
    {
      label: 'Complete your organization profile',
      percent: profilePercent,
      complete: profilePercent === 100,
      icon: Building2,
      color: '#18365E',
      onClick: onEditProfile,
      ctaLabel: profilePercent === 100 ? 'View Profile' : 'Edit Profile'
    },
    {
      label: 'Fill out strategic surveys',
      percent: surveyPercent,
      complete: surveyPercent === 100,
      icon: ClipboardCheck,
      color: '#FF6E4C',
      detail: `${surveyPercent}% complete`,
      onClick: onContinueSurveys,
      ctaLabel: surveyPercent === 100 ? 'Review Surveys' : 'Continue Surveys'
    },
    {
      label: 'Upload strategic documents',
      percent: hasDocuments ? 100 : 0,
      complete: hasDocuments,
      icon: FileStack,
      color: '#2563eb',
      detail: documentCount > 0 ? `${documentCount} document${documentCount > 1 ? 's' : ''} uploaded` : 'No documents yet',
      onClick: onManageDocuments,
      ctaLabel: hasDocuments ? 'Manage Documents' : 'Upload Documents'
    }
  ];

  return (
    <Card className="relative p-8 bg-gradient-to-br from-white via-[#18365E]/5 to-[#FF6E4C]/5 border-2 border-[#18365E]/20 shadow-lg hover:shadow-xl transition-all overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#FF6E4C]/20 to-transparent rounded-full blur-3xl -z-0"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#18365E]/20 to-transparent rounded-full blur-3xl -z-0"></div>
      
      <div className="relative z-10">
        {/* Checklist with Circular Progress - Starts immediately (no header) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const circumference = 2 * Math.PI * 20;
            const strokeDashoffset = circumference - (step.percent / 100) * circumference;
            
            return (
              <button
                key={idx}
                onClick={step.onClick}
                className="relative p-5 rounded-xl bg-slate-50 hover:bg-slate-100 border-2 border-slate-200 hover:border-slate-300 transition-all hover:scale-105 hover:shadow-lg text-left group cursor-pointer"
              >
                {/* Circular Progress */}
                <div className="flex items-center gap-4 mb-3">
                  <div className="relative flex-shrink-0">
                    <svg className="w-14 h-14 transform -rotate-90">
                      <circle
                        cx="28"
                        cy="28"
                        r="20"
                        stroke="currentColor"
                        strokeWidth="3"
                        fill="none"
                        className="text-slate-200"
                      />
                      <circle
                        cx="28"
                        cy="28"
                        r="20"
                        stroke={step.color}
                        strokeWidth="3"
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        className="transition-all duration-500"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      {step.complete ? (
                        <CheckCircle2 className="w-6 h-6" style={{ color: step.color }} />
                      ) : (
                        <Icon className="w-6 h-6 text-slate-400 group-hover:text-slate-600 transition-colors" />
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-[24px] font-bold leading-none" style={{ color: step.color }}>
                      {step.percent}%
                    </div>
                    {step.complete && (
                      <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: step.color }}>
                        Done!
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-[14px] font-semibold text-[#18365E] mb-1 leading-snug">
                  {step.label}
                </div>
                
                {step.detail && (
                  <div className="text-[12px] text-slate-500 mb-3">
                    {step.detail}
                  </div>
                )}

                <div className="flex items-center gap-1 text-[13px] font-semibold group-hover:gap-2 transition-all" style={{ color: step.color }}>
                  <span>{step.ctaLabel}</span>
                  <ExternalLink className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom Section: Progress Bar + Button */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[14px] font-semibold text-[#18365E]">Your Progress</span>
              <span className="text-[14px] font-bold text-[#FF6E4C]">{overallPercent}% Complete</span>
            </div>
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden shadow-inner">
              <div 
                className="h-full bg-gradient-to-r from-[#18365E] via-[#FF6E4C] to-[#FF6E4C] rounded-full transition-all duration-700 relative"
                style={{ width: `${overallPercent}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
              </div>
            </div>
          </div>

          <Button
            onClick={onGenerateSummary}
            disabled={!canGenerate || isGenerating}
            className={`group relative overflow-hidden md:w-auto w-full transition-all duration-300 ${
              canGenerate 
                ? 'bg-gradient-to-r from-[#18365E] to-[#1a4075] hover:from-[#FF6E4C] hover:to-[#ff5533] text-white shadow-lg hover:shadow-2xl hover:scale-105' 
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
            style={{ 
              minHeight: '56px',
              fontSize: '15px',
              fontWeight: 600,
              padding: '0 32px'
            }}
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Generate Summary
                <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
              </>
            )}
            
            {canGenerate && !isGenerating && (
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF6E4C] to-[#18365E] opacity-0 group-hover:opacity-20 transition-opacity"></div>
            )}
          </Button>
        </div>
        
        {!canGenerate && (
          <p className="text-[13px] text-slate-500 text-center mt-3">
            Complete at least 75% to generate your personalized summary
          </p>
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </Card>
  );
}